import * as THREE from 'three';
import { GameMode, GameModeContext } from './GameMode';
import { WaveManager } from './WaveManager';
import { WaveHUD } from '../ui/WaveHUD';
import { EventBus } from '../core/EventBus';

export class WaveMode extends GameMode {
  private waveManager: WaveManager;
  private waveHUD: WaveHUD;
  private score: number = 0;
  private highScore: number = 0;

  constructor(context: GameModeContext) {
    super(context);

    this.waveManager = new WaveManager(
      context.scene,
      context.projectilePool,
      context.weaponManager,
      context.playerMesh
    );

    this.waveHUD = new WaveHUD();

    // Load high score from localStorage
    this.highScore = parseInt(localStorage.getItem('waveMode_highScore') || '0', 10);

    this.setupEventListeners();
  }

  public getName(): string {
    return 'Wave Survival';
  }

  private setupEventListeners(): void {
    // Track score
    EventBus.on('score:added', (data) => {
      if (!this.isActive) return;
      this.score += data.points;
      this.waveHUD.updateScore(this.score);

      // Update high score
      if (this.score > this.highScore) {
        this.highScore = this.score;
        localStorage.setItem('waveMode_highScore', this.highScore.toString());
        this.waveHUD.updateHighScore(this.highScore);
      }
    });

    // Track target destruction for points
    EventBus.on('target:destroyed', (data) => {
      if (!this.isActive) return;
      EventBus.emit('score:added', {
        points: data.points,
        reason: 'enemy_destroyed',
      });
    });

    // Player death
    EventBus.on('player:died', () => {
      if (!this.isActive) return;
      this.onPlayerDeath();
    });
  }

  protected onStart(): void {
    this.score = 0;
    this.waveHUD.show();
    this.waveHUD.updateScore(0);
    this.waveHUD.updateHighScore(this.highScore);
    this.waveManager.begin();
  }

  protected onStop(): void {
    this.waveHUD.hide();
    this.waveManager.reset();
  }

  public update(dt: number): void {
    if (!this.isActive) return;

    this.waveManager.update(dt);

    // Update HUD based on wave state
    const state = this.waveManager.getState();
    const wave = this.waveManager.getCurrentWave();
    const enemyCount = this.waveManager.getEnemyCount();

    switch (state) {
      case 'rest':
        const restTime = Math.ceil(this.waveManager.getRestTimer());
        if (wave === 0) {
          this.waveHUD.showMessage(`GET READY`, `Starting in ${restTime}...`);
        } else {
          this.waveHUD.showMessage(`WAVE ${wave + 1}`, `Starting in ${restTime}...`);
        }
        break;

      case 'spawning':
      case 'active':
        this.waveHUD.updateWave(wave, enemyCount);
        this.waveHUD.hideMessage();
        break;

      case 'complete':
        this.waveHUD.showMessage(`WAVE ${wave} COMPLETE!`, `+${wave * 500} bonus`);
        break;

      case 'gameover':
        this.waveHUD.showGameOver(this.score, this.highScore, wave);
        break;
    }
  }

  private onPlayerDeath(): void {
    this.waveManager.setGameOver();
  }

  public restart(): void {
    this.score = 0;
    this.waveHUD.hideGameOver();
    this.waveHUD.updateScore(0);
    this.waveManager.begin();
  }

  public getWaveManager(): WaveManager {
    return this.waveManager;
  }

  public getScore(): number {
    return this.score;
  }

  public getHighScore(): number {
    return this.highScore;
  }

  public getEnemyPositions(): THREE.Vector3[] {
    return this.waveManager.getAllEnemyPositions();
  }
}
